import React, { useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { X } from 'lucide-react';

/**
 * ModalShell — Reusable modal/dialog wrapper (presentational).
 *
 * Provides the backdrop, centering, scrollable inner panel, close button,
 * and an optional logo/title header. This component knows nothing about
 * form state — it just renders `children`.
 *
 * @param {string}    title        - Modal heading text
 * @param {Function}  onClose      - Close handler
 * @param {string}    [maxWidth]   - Tailwind max-w class (default: 'max-w-2xl')
 * @param {ReactNode} [headerIcon] - Optional icon/logo to the left of the title
 * @param {ReactNode} [subtitle]   - Optional subtitle below title
 * @param {boolean}   [blurBackdrop] - Whether to apply backdrop-blur
 * @param {string}    [titleColor] - Tailwind text color for the title
 *
 * @example
 *   <ModalShell title="Add New Farmer" onClose={onClose} maxWidth="max-w-4xl">
 *     <form> ... </form>
 *   </ModalShell>
 */
export function ModalShell({
  title,
  onClose,
  children,
  maxWidth = 'max-w-2xl',
  headerIcon,
  subtitle,
  blurBackdrop = false,
  titleColor = 'text-amber-900',
  disabled = false,
}) {
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape' && !disabled) onClose();
  }, [onClose, disabled]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50
        ${blurBackdrop ? 'backdrop-blur-sm' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className={`bg-white rounded-2xl ${maxWidth} w-full max-h-[90vh] overflow-y-auto shadow-2xl my-8`}>
        <Card className="border-0 shadow-none">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {headerIcon}
                <CardTitle className={titleColor}>{title}</CardTitle>
              </div>
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={disabled}
                className="text-amber-900 hover:text-white bg-amber-100 hover:bg-amber-700 rounded-full h-10 w-10 p-0 transition-all duration-200"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            {subtitle && <div className="mt-1">{subtitle}</div>}
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ModalShell;
